import clsx from 'clsx';
import React, { useRef } from 'react';

import IconProTagThumbnail from '@components/Images/Rebranding/IconProTagThumbnail';
import useLazyImageSwap from '@hooks/useLazyImageSwap';
import { upgrade_subscription_path } from '@routes';

import type { GraphicsSearchPageResultProps } from './interfaces';

const GraphicsSearchPageResult = ({
  graphic,
  handleGraphicClick,
  userIsPro,
}: GraphicsSearchPageResultProps) => {
  const figureRef = useRef<HTMLElement>(null);
  const imgRef = useRef<HTMLImageElement>(null);
  const { id, imageUrl, isPro, publishDate, title, url } = graphic;
  const { aspectRatio } = useLazyImageSwap({
    figureRef,
    imageSrc: imageUrl,
    imageRef: imgRef,
  });
  const showProUpgrade = !userIsPro && isPro;

  return (
    <figure
      className="animate-fade-in m-0 mb-32 break-inside-avoid last-of-type:mb-0"
      ref={figureRef}
    >
      <figcaption>
        <time
          className="font-suisse-screen text-disclosure-lg font-normal text-grayscale-gray"
          dateTime={publishDate}
        >
          {publishDate}
          {showProUpgrade ? (
            <span className="text-disclosure-sm">
              {' '}
              — (This graphic is for Pro subscribers only.)
            </span>
          ) : (
            ''
          )}
        </time>
      </figcaption>
      <a
        className={clsx(
          'group relative block overflow-hidden px-16',
          showProUpgrade ? 'bg-grayscale-charcoal' : 'bg-white'
        )}
        href={showProUpgrade ? upgrade_subscription_path() : url}
        onClickCapture={() => handleGraphicClick(graphic)}
        rel="noopener"
        target="_blank"
        data-testid={`graphics-masonry-result-${id}`}
      >
        {isPro && <IconProTagThumbnail className="absolute right-0 top-0 z-10" />}
        <img
          alt={`${title} thumbnail`}
          className={clsx(
            'block h-auto w-full object-cover object-top transition duration-200',
            '[backface-visibility:hidden] group-hover:scale-[1.03]'
          )}
          data-src={imageUrl}
          loading="lazy"
          ref={imgRef}
          src=""
          style={aspectRatio ? { aspectRatio } : undefined}
        />
      </a>
    </figure>
  );
};

export default GraphicsSearchPageResult;
